import React from 'react'
import { X, Check } from 'react-feather'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import Modal from '../../components/Modal'
import { AutoColumn } from '../../components/Column'
import { RowBetween } from '../../components/Row'
import { Text } from 'rebass'

const ModalContent = styled.div`
  width: 100%;
  padding: 0;
`

const Header = styled(RowBetween)`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.bg3};
`

const CloseIcon = styled(X)`
  cursor: pointer;
  color: ${({ theme }) => theme.text2};

  :hover {
    color: ${({ theme }) => theme.text1};
  }
`

const List = styled(AutoColumn)`
  padding: 0;
`

const FeeItem = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.125rem 1.5rem;
  cursor: pointer;
  background-color: ${({ theme, selected }) => (selected ? theme.bg2 : 'transparent')};
  transition: all 0.2s ease;
  border-bottom: 1px solid ${({ theme }) => theme.bg2};

  :hover {
    background-color: ${({ theme }) => theme.bg2};
  }

  :last-of-type {
    border-bottom: none;
  }
`

const FeeLabel = styled(Text)<{ selected: boolean }>`
  font-size: 16px;
  font-weight: ${({ selected }) => (selected ? 600 : 400)};
  color: ${({ theme, selected }) => (selected ? theme.text1 : theme.text2)};
`

const FeeItemRight = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  gap: 10px;
`

const FeeSuffix = styled(Text)`
  font-size: 12px;
  color: ${({ theme }) => theme.text2};
`

const CheckIcon = styled(Check)`
  color: ${({ theme }) => theme.primary1};
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`

const ExplainSection = styled.div`
  padding: 1rem 1.5rem 1.25rem;
  border-bottom: 1px solid ${({ theme }) => theme.bg3};
`

const ExplainText = styled(Text)`
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.text2};
`

const ExplainTextBold = styled(Text)`
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.text2};
  font-weight: 700;
  margin-top: 8px;
`

export interface FeeOption {
  value: string
  label: string
  /** 可选，说明文案，居右显示（如 1% 的「冰山单不对外公开」） */
  suffix?: string
}

interface FeeRateModalProps {
  isOpen: boolean
  onDismiss: () => void
  value: string
  options: FeeOption[]
  onSelect: (value: string) => void
}

export default function FeeRateModal({ isOpen, onDismiss, value, options, onSelect }: FeeRateModalProps) {
  const { t } = useTranslation()

  const handleSelect = (v: string) => {
    onSelect(v)
    onDismiss()
  }

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} maxHeight={90} centerOnMobile stickToTopOnMobile>
      <ModalContent>
        <Header>
          <Text fontWeight={500} fontSize={18}>
            {t('orderFeeRateModalTitle')}
          </Text>
          <CloseIcon onClick={onDismiss} />
        </Header>
        <ExplainSection>
          <ExplainText>{t('orderFeeRateExplain')}</ExplainText>
          <ExplainText as="p" style={{ marginTop: 8 }}>{t('orderFeeRateExplainExample')}</ExplainText>
          <ExplainText as="p" style={{ marginTop: 8 }}>{t('orderFeeRateExplainBuyOnly')}</ExplainText>
          <ExplainTextBold>{t('orderFeeRateExplainPriority')}</ExplainTextBold>
        </ExplainSection>
        <List gap="0">
          {options.map(opt => (
            <FeeItem key={opt.value} selected={value === opt.value} onClick={() => handleSelect(opt.value)}>
              <FeeLabel selected={value === opt.value}>{opt.label}</FeeLabel>
              <FeeItemRight>
                {opt.suffix && <FeeSuffix>{opt.suffix}</FeeSuffix>}
                {value === opt.value && <CheckIcon />}
              </FeeItemRight>
            </FeeItem>
          ))}
        </List>
      </ModalContent>
    </Modal>
  )
}
